import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import type {
  AdminEntity,
  AdminRow,
  AppNoteRow,
  ApplicationRow,
  InquiryRow,
  NewsletterRow,
  SubscriberRow,
} from "./types"

async function responseData<T>(response: { ok: boolean; json: () => Promise<unknown> }): Promise<T> {
  const body = (await response.json()) as { data?: T; message?: string; error?: unknown }
  if (!response.ok || !body.data) throw new Error(getErrorMessage(body, "Request failed"))
  return body.data
}

export async function fetchAdminRows(entity: AdminEntity): Promise<AdminRow[]> {
  switch (entity) {
    case "inquiry": {
      const result = await responseData<{ items: AdminRow[]; total: number }>(
        await client.api.admin.data.inquiries.$get({ query: {} }),
      )
      return result.items
    }
    case "application":
      return responseData(await client.api.admin.data.applications.$get())
    case "appNote":
      return responseData(await client.api.admin.data.notes.$get())
    case "subscriber":
      return responseData(await client.api.admin.data.subscribers.$get())
    case "newsletter":
      return responseData(await client.api.admin.data.newsletters.$get())
    case "delivery":
      return responseData(await client.api.admin.data.deliveries.$get())
  }
}

export async function updateInquiryStatus(
  id: string,
  status: InquiryRow["status"],
): Promise<InquiryRow> {
  return responseData(await client.api.admin.data.inquiries[":id"].$patch({
    param: { id },
    json: { status },
  }))
}

export async function updateApplicationStatus(
  id: string,
  status: ApplicationRow["status"],
): Promise<ApplicationRow> {
  return responseData(await client.api.admin.data.applications[":id"].$patch({
    param: { id },
    json: { status },
  }))
}

export async function createApplicationNote(input: {
  id: string
  applicationId: string
  body: string
}): Promise<AppNoteRow> {
  return responseData(await client.api.admin.data.notes.$post({ json: input }))
}

export async function saveNewsletter(
  id: string,
  input: Pick<NewsletterRow, "title" | "subject" | "body" | "status">,
): Promise<NewsletterRow> {
  return responseData(await client.api.admin.data.newsletters[":id"].$put({
    param: { id },
    json: input,
  }))
}

export async function deleteNewsletter(id: string): Promise<NewsletterRow> {
  return responseData(await client.api.admin.data.newsletters[":id"].$delete({ param: { id } }))
}

export async function deleteSubscriber(id: string): Promise<SubscriberRow> {
  return responseData(await client.api.admin.data.subscribers[":id"].$delete({ param: { id } }))
}
